import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { DynamicPricingMode, PricingConfigurationValidationResponse } from '@/shared/api';
import { serverIssues } from './configurationIssueText';
import { goToSection } from './ConfigurationSectionNavigation';
import { clientIssues } from './configurationClientIssues';
import {
  SECTION_KEYS,
  changedSections,
  sectionMarkers,
  type FieldIssue,
} from './configurationSections';
import type { PricingConfigurationFormValues } from './pricingConfigurationSchema';

const UNEXPLAINED: FieldIssue = {
  field: 'mode',
  section: 'status',
  message: 'Der Server hat den Entwurf abgelehnt, ohne einen Grund zu nennen.',
  source: 'server',
};

/**
 * Was Formular und Server am aktuellen Entwurf beanstanden. Ein Serverbefund gilt nur für genau den
 * Entwurf, zu dem die Antwort kam; nach jeder Eingabe tritt er zurück, bis neu geprüft wird.
 */
export function useConfigurationIssues(
  form: UseFormReturn<PricingConfigurationFormValues>,
  draftKey: string,
  mode: DynamicPricingMode,
  validation?: PricingConfigurationValidationResponse,
) {
  const { t } = useTranslation();
  const [attempted, setAttempted] = useState(false);
  const [validatedKey, setValidatedKey] = useState<string | null>(null);
  const [jump, setJump] = useState(0);
  const handledJump = useRef(0);
  const translate = (message: string) =>
    t(`admin.dynamicPricing.configuration.validation.${message}`, { defaultValue: message });
  const client = clientIssues(form.formState.errors, translate);
  const fresh = Boolean(validation) && validatedKey === draftKey;
  const blockers = mode === 'Off' ? [] : (validation?.previewBlockers ?? []);
  const rejected =
    fresh && (!validation!.canSavePaused || (mode !== 'Off' && !validation!.isPreviewReady));
  const mapped = fresh ? serverIssues(validation!.saveErrors, blockers) : [];
  const server = rejected && mapped.length === 0 ? [UNEXPLAINED] : mapped;
  const changed = changedSections(form.formState.dirtyFields);
  const markers = sectionMarkers(changed, [...client, ...server]);
  const serverByField: Record<string, string> = {};
  for (const issue of server) serverByField[issue.field] ??= issue.message;
  const firstServerSection = server[0]?.section;

  // Nur eine neue Ablehnung springt; späteres Tippen soll die Seite nicht verschieben. Fehler des
  // Formulars fokussiert react-hook-form selbst.
  useEffect(() => {
    if (jump === handledJump.current) return;
    handledJump.current = jump;
    if (firstServerSection) goToSection(firstServerSection);
  }, [jump, firstServerSection]);

  return {
    changed: SECTION_KEYS.filter((key) => changed.has(key)),
    markers,
    serverByField,
    serverErrors: server.length,
    serverChecked: fresh && !rejected,
    summary: server.length > 0 ? server : attempted ? client : [],
    summaryFromServer: server.length > 0,
    /** Mit dem Entwurf zum Zeitpunkt des Klicks – nicht mit dem nach der Antwort. */
    markValidated: (key: string) => setValidatedKey(key),
    rejected: (source: 'client' | 'server') =>
      source === 'client' ? setAttempted(true) : setJump((count) => count + 1),
    reset: () => {
      setAttempted(false);
      setValidatedKey(null);
    },
  };
}
